import React, {useMemo} from "react";
import {Controller, useFormContext} from "react-hook-form";
import useTranslate from "../../../../core/languages/hooks/useTranslate";
import Prebooking from "../../../../booking/types/Prebooking";
import HotelRoom from "../../../../hotels/types/HotelRoom";
import HotelRoomRate from "../../../../hotels/types/HotelRoomRate";
import Loader from "../../../../ui/shared/Loader/Loader";
import HotelWebRoomCard from "../../cards/HotelWebRoomCard";
import EventCity from "../../../../eventCities/types/EventCity";
import moment from "moment/moment";
import useHotelGetRoomRatesInfo from "../../../../hotels/hooks/useHotelGetRoomRatesInfo";
import useHotelGetRoomsInfo from "../../../../hotels/hooks/useHotelGetRoomsInfo";
import useGetBasketItems from "../../../../booking/hooks/useGetBasketItems";
import {WizardSaveInput} from "../../../types/WizardSaveInput";
import {extractIdFromPath} from "../../../../transfers/utils/extractIdFromPath";

interface StepSelectMoreRoomsProps {
  preBooking: Prebooking;
  eventCity: EventCity;
  currencySymbol: string;
}

const StepSelectMoreRooms: React.FC<StepSelectMoreRoomsProps> = ({preBooking, eventCity, currencySymbol}) => {
  const t = useTranslate();
  const {control, formState: {errors}} = useFormContext<WizardSaveInput>();
  const startDate = useMemo(() => moment(eventCity?.startDate).format('YYYY-MM-DD'), [eventCity?.startDate]);
  const endDate = useMemo(() => moment(eventCity?.endDate).format('YYYY-MM-DD'), [eventCity?.endDate]);
  const webHotelRoomsQuery = useHotelGetRoomsInfo(
    extractIdFromPath(preBooking.prebookingHotel?.hotelEvent), startDate, endDate, {
    execute: !!preBooking.prebookingHotel?.hotelEvent
  });
  const preBookingHotelRoomsQuery = useGetBasketItems(preBooking.internalId, { execute: !!preBooking.internalId  });
  const {data: preBookingHotelRooms} = preBookingHotelRoomsQuery;
  const {data: webHotelRooms} = webHotelRoomsQuery;
  const webHotelRoomsIds = useMemo(() => webHotelRooms?.map((room: HotelRoom) => room.id), [webHotelRooms]);

  const webHotelRoomsRatesQuery = useHotelGetRoomRatesInfo(webHotelRoomsIds, startDate, endDate,
    {execute: webHotelRoomsIds !== undefined && webHotelRoomsIds.length > 0}
  )
  const {data: webHotelRoomRates, isLoading: isWebHotelRoomRatesLoading} = webHotelRoomsRatesQuery;

  if (isWebHotelRoomRatesLoading) {
    return (
      <div className="nc-PackageWizard flex flex-col relative w-full">
        <div className="flex justify-center py-16">
          <Loader/>
        </div>
      </div>
      )
  }


  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
          {t("packages.fields.addMoreRooms")}
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
          {t("packages.validation.addMoreIfYouNeed")}
        </p>
      </div>

      <Controller
        name="selectedRoomId"
        control={control}
        render={({field}) => (
          <div className={`p-4 rounded-lg transition ${
            errors.selectedRoomId
              ? "border-2 border-red-500 bg-red-50 dark:bg-red-950"
              : "border border-transparent"
          }`}>
            <div className="flex-grow mt-4 lg:mt-4">
              <div className="sticky top-28 grid grid-cols-1 lg:grid-cols-2 gap-2">
                {webHotelRooms && webHotelRoomRates ?
                  webHotelRooms.map((room: HotelRoom) => (
                    <HotelWebRoomCard
                      key={room.id}
                      preBooking={preBooking}
                      eventCity={eventCity}
                      hotelRoom={room}
                      roomPrice={
                        webHotelRoomRates
                          .filter((roomRate: HotelRoomRate) => roomRate.roomHotelEvent === `/room_hotel_events/${room.id}`)
                          .reduce((acc: number, item: HotelRoomRate) => acc + item.loveRate, 0)
                      }
                      preBookingHotelRooms={preBookingHotelRooms}
                      currencySymbol={currencySymbol}
                    />
                  ))
                  : <Loader centerHorizontally/>
                }
              </div>
            </div>
          </div>
        )}
      />

      {errors.selectedRoomId && (
        <div className="bg-red-50 dark:bg-red-950 border-2 border-red-500 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300 font-medium flex items-center">
            <i className="las la-exclamation-circle mr-2"></i>
            {errors.selectedRoomId.message}
          </p>
        </div>
      )}
    </div>
  );
};

export default StepSelectMoreRooms;


